import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getOpenShifts } from '../../api/shifts-client';
import { Shift } from '../../interfaces/shift-interfaces';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';

/**
 * ONE SOURCE OF TRUTH FOR "AM I CLOCKED IN".
 *
 * ── WHY THIS EXISTS ──
 * Two surfaces now depend on the answer: the clock card renders it, and the main
 * menu withholds the controls that record work until it is yes. When the card
 * owned the state privately, the menu could only be told by a callback — which
 * works, but it makes the card the only thing allowed to ask, and the next
 * screen that needs the answer either duplicates the fetch (two readers, two
 * moments, one of them stale) or grows another prop threaded through the tree.
 *
 * So the question is asked ONCE, here, and everything reads the same answer.
 *
 * ── THE THREE STATES, AND WHY null IS NOT false ──
 *   null   the first read has not answered yet.
 *   false  answered: no open shift at that building.
 *   true   answered: on shift there.
 * A consumer that collapses null into false enables a screenful of controls and
 * then takes them away a moment later, which reads as the app changing its mind.
 *
 * ── IT DOES NOT FETCH FOR PEOPLE WHO HAVE NO CLOCK ──
 * `shift:clock` is what the endpoint checks, so without it this would be a
 * guaranteed 403 on every page load. Those users simply get null forever, and
 * the menu's gate does not apply to them (see main-menu.tsx).
 */
interface ShiftContextValue {
  /** Every shift this user has open, at any building. null until first answer. */
  openShifts: Shift[] | null;
  /** null = not known yet. */
  isOnShiftAt: (buildingId: number) => boolean | null;
  /** Re-read after clocking in or out. Returns when the state is current. */
  refresh: () => Promise<void>;
  /**
   * THE ARRIVAL, held here rather than inside <ShiftClock>.
   *
   * ── THE `receipt` THAT USED TO SIT HERE IS GONE (2026-09-04) ──
   * It held the handover the outgoing person had just written, so the card could
   * raise it as a receipt. Handing over now ENDS THE SESSION, so there is no
   * screen left to raise it on: the next thing the reader sees is the sign-in
   * card. The state, the layer and the copy went together.
   *
   * The trap it was here to avoid is real and still applies to what remains,
   * which is why the note survives its subject.
   *
   * Clocking IN flips the menu the other way — from the gate (clock card in the
   * middle of the screen) to on-shift (clock card in the title column) — so the
   * card that made the request is unmounted by the response it was waiting for.
   * `takeoverArmed` as local state was therefore set and destroyed in the same
   * commit, and the handover the desk had just been handed never appeared, even
   * though the claim itself had succeeded in the database.
   *
   * That is the whole argument for this living in the provider rather than in
   * the card: the card cannot hold state across the commit that unmounts it, and
   * the next person to add state there would rediscover it the hard way.
   *
   * It lives for one act: armed by a clock-in that actually claimed something,
   * cleared when the reader acknowledges it.
   */
  takeoverArmed: boolean;
  setTakeoverArmed: (armed: boolean) => void;
}

const ShiftContext = createContext<ShiftContextValue | null>(null);

export const ShiftProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { permissions } = useAuth();
  const mayClock = can(permissions, PERMISSIONS.SHIFT_CLOCK);
  const [openShifts, setOpenShifts] = useState<Shift[] | null>(null);
  const [takeoverArmed, setTakeoverArmed] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    if (!mayClock) {
      setOpenShifts(null);

      return;
    }
    try {
      const res = await getOpenShifts();
      setOpenShifts(res.open_shifts || []);
    } catch {
      // A failed read is not a failed shift. Answer "no open shift" rather than
      // an error state no desk can act on — the clock's own 409 path recovers
      // the truth if this was wrong, and the menu's gate fails CLOSED, which is
      // the safe direction for a rule about recording work.
      setOpenShifts([]);
    }
  }, [mayClock]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<ShiftContextValue>(
    () => ({
      openShifts,
      isOnShiftAt: (buildingId: number) =>
        openShifts === null
          ? null
          : openShifts.some(s => s.building_id === buildingId),
      refresh,
      takeoverArmed,
      setTakeoverArmed,
    }),
    [openShifts, refresh, takeoverArmed]
  );

  return (
    <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>
  );
};

/**
 * Safe outside the provider: a component rendered in a test harness or on a
 * public page gets "not known", never a crash. The menu treats that as
 * not-yet-cleared and the clock card renders its neutral state.
 */
export const useShifts = (): ShiftContextValue => {
  const ctx = useContext(ShiftContext);
  if (ctx) return ctx;

  return {
    openShifts: null,
    isOnShiftAt: () => null,
    refresh: async () => undefined,
    takeoverArmed: false,
    setTakeoverArmed: () => undefined,
  };
};
